import axios from "axios";
import { load } from "opentype.js";

export const axiosReq = ({
  method,
  url,
  headers = {},
  withCredentials = true,
  data = null,
  params,
}) => {
  return axios({
    method,
    url,
    headers,
    withCredentials,
    data,
    params,
  });
};

export const splitId = (id) => {
  return id.slice(0, 3) + "-" + id.slice(-3);
};

export function formatDate(isoString) {
  const date = new Date(isoString);

  // Get parts of the date
  const day = date.getDate();
  const month = date.toLocaleString("default", { month: "long" }); // Full month name
  const year = date.getFullYear();

  // Format the time
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12; // Convert to 12-hour format

  return `${day} ${month} ${year} - ${hours}:${minutes}:${seconds} ${ampm}`;
}

export function isWithinOneMinute(timestamp1, timestamp2) {
  const date1 = new Date(timestamp1);
  const date2 = new Date(timestamp2);

  // Extract year, month, day, hour, and minute for both timestamps
  const date1Key = {
    year: date1.getFullYear(),
    month: date1.getMonth(),
    day: date1.getDate(),
    hour: date1.getHours(),
    minute: date1.getMinutes(),
  };

  const date2Key = {
    year: date2.getFullYear(),
    month: date2.getMonth(),
    day: date2.getDate(),
    hour: date2.getHours(),
    minute: date2.getMinutes(),
  };

  // Check if they're exactly one minute apart (ignoring seconds)
  if (
    date1Key.year === date2Key.year &&
    date1Key.month === date2Key.month &&
    date1Key.day === date2Key.day &&
    date1Key.hour === date2Key.hour &&
    Math.abs(date1Key.minute - date2Key.minute) >= 1
  ) {
    return true;
  }

  return false;
}

export const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp * 1000); // Convert to milliseconds
  const options = {
    timeZone: "UTC", // Adjust to the correct time zone if needed
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };
  return date.toLocaleString("en-US", options);
};

export const calculateSignature = async (params) => {
  const secret = process.env.REACT_APP_SECRET;

  // Step 1: Sort parameters alphabetically by key
  const sortedParams = Object.keys(params)
    .sort()
    .map((key) => params[key])
    .join("");

  // Step 2: Append the secret to the sorted values
  const dataToHash = `${sortedParams}${secret}`;

  // Step 3: Calculate SHA256 hash
  const hash = await getSHA256Hash(dataToHash);

  return hash;
};

const getSHA256Hash = async (input) => {
  const textAsBuffer = new TextEncoder().encode(input);
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", textAsBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hash = hashArray
    .map((item) => item.toString(16).padStart(2, "0"))
    .join("");
  return hash;
};

export const extractFirstAndLast = (name) => {
  const words = name.trim().split(/\s+/);
  return words.length > 1 ? `${words[0]} ${words[words.length - 1]}` : name;
};

export async function generateTextPath(text, options = {}) {
  const {
    fontUrl,
    fontSize = 60,
    trim = false,
    center = false,
    containerWidth = 1080,
    containerHeight = 1080,
    justPath = false,
  } = options;

  // Optionally trim the text to only the important parts.
  const processedText = trim ? extractFirstAndLast(text) : text;

  // Load the font file (must be a real font file served from your public folder).
  const font = await load(fontUrl);

  // If just the raw path is needed, simply return it.
  if (justPath) {
    const rawPath = font.getPath(processedText, 0, fontSize, fontSize);
    return rawPath.toPathData();
  }

  // --- PASS 1: Measure the text at the base font size ---
  let tempPath = font.getPath(processedText, 0, 0, fontSize);
  let box = tempPath.getBoundingBox();
  const measuredWidth = box.x2 - box.x1;
  const measuredHeight = box.y2 - box.y1;

  // --- Scaling: If centering is requested, scale the text down if it's too big.
  let finalFontSize = fontSize;

  // --- PASS 2: Build the path with the final font size ---
  let finalPath = font.getPath(processedText, 0, 0, finalFontSize);
  let finalBox = finalPath.getBoundingBox();
  const finalWidth = finalBox.x2 - finalBox.x1;
  const finalHeight = finalBox.y2 - finalBox.y1;

  // --- Centering: Compute offsets to center the text inside the container.
  // If centering is not requested, the offsets will remain 0.
  let xOffset = 0;
  let yOffset = 0;
  if (center) {
    // Subtract finalBox.x1 and finalBox.y1 in case the path starts with negative coordinates.
    xOffset = (containerWidth - finalWidth) / 2 - finalBox.x1;
    yOffset = (containerHeight - finalHeight) / 2 - finalBox.y1 + 210;
  }

  // Build the final path with computed offsets.
  finalPath = font.getPath(processedText, xOffset, yOffset, finalFontSize);

  // Return the SVG path data (the "d" attribute).
  return finalPath.toPathData();
}
